import {
  DependencyList,
  EffectCallback,
  RefObject,
  useEffect,
  useRef,
  useState,
} from "react";
import { throttle } from "throttle-debounce";
import { setModuleSetting } from "../slices/modules/thunks.js";
import { useAppDispatch } from "../store/hooks.js";
import {
  ModuleSettingsTypes,
  PartialModuleSettings,
  TranslatedModule,
} from "../types/index.js";

type ScrollDirection = "up" | "down";

export const useScrollDirection = (): ScrollDirection | undefined => {
  const prevScrollYRef = useRef(0);
  const [dir, setDir] = useState<ScrollDirection>();

  useEffect(() => {
    prevScrollYRef.current = window.scrollY;

    const onScroll = () => {
      const { scrollY } = window;
      const newDir = scrollY > prevScrollYRef.current ? "down" : "up";
      scrollY !== prevScrollYRef.current && setDir(newDir);
      prevScrollYRef.current = scrollY;
    };

    const onScrollThrottled = throttle(250, onScroll);

    window.addEventListener("scroll", onScrollThrottled);

    return () => {
      window.removeEventListener("scroll", onScrollThrottled);
    };
  }, []);

  return dir;
};

// Extend this list as necessary
type Key = "Escape" | "Delete" | "ArrowUp" | "ArrowDown";

export const useKeyDown = (key: Key, callback: () => void) => {
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => event.key === key && callback();
    document.addEventListener("keydown", onKeyDown, { passive: true });

    return () => {
      document.removeEventListener("keydown", onKeyDown);
    };
  }, []);
};

// Inspired by https://github.com/streamich/react-use/blob/master/src/useIntersection.ts
export const useIntersection = (
  ref: RefObject<HTMLElement | null>,
  options?: IntersectionObserverInit,
): IntersectionObserverEntry | null => {
  const [entry, setEntry] = useState<IntersectionObserverEntry | null>(null);

  useEffect(() => {
    if (!ref.current) {
      return () => {};
    }

    const handler = (entries: IntersectionObserverEntry[]) => {
      entries[0] && setEntry(entries[0]);
    };

    const observer = new IntersectionObserver(handler, options);
    observer.observe(ref.current);

    return () => {
      setEntry(null);
      observer.disconnect();
    };
  }, [ref.current, options?.threshold, options?.root, options?.rootMargin]);

  return entry;
};

/**
 * Same as `useEffect`, but doesn’t fire on the first render,
 * only on the ≥ 2nd renders.
 * This allows make the effect fire only if the dependencies change.
 */
export const useDidMountEffect = (
  effect: EffectCallback,
  deps: DependencyList,
) => {
  const didMount = useRef(false);

  useEffect(() => {
    didMount.current ? effect() : (didMount.current = true);
  }, deps);
};

export const useSetModuleSetting = <Settings extends ModuleSettingsTypes>(
  translatedModule: TranslatedModule<Settings>,
) => {
  const dispatch = useAppDispatch();
  return (settings: PartialModuleSettings<Settings>) =>
    dispatch(setModuleSetting<Settings>(translatedModule, settings));
};
